import { BrowserModule, HammerModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { CUSTOM_ELEMENTS_SCHEMA, NgModule } from '@angular/core';

import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { HttpClientModule, HTTP_INTERCEPTORS } from '@angular/common/http';

import { AppMaterialModule } from 'modules/material/material.module';
import { JFlexLayoutModule } from 'modules/flex-layout/flex-layout.module';
import { NgxWebstorageModule } from 'ngx-webstorage';

import { AppComponent } from './app.component';
import { AppRoutingModule } from './routing/app-routing.module';
import { MainLayoutComponent } from './layouts/main-layout/main-layout.component';
import { NavbarComponent } from './navbar/navbar.component';
import { HomeComponent } from './home/home.component';
import { PageNotFoundComponent } from './page-not-found/page-not-found.component';
import { UsersComponent } from './user-roles/users/users.component';
import { LoginComponent } from './login/login.component';
import { LoginLayoutComponent } from './layouts/login-layout/login-layout.component';
import { ToolbarComponent } from './toolbar/toolbar.component';
import { EventsComponent } from './events/events.component';
import { CategoryTagsComponent } from './categories/category-tags/category-tags.component';
import { EventDetailsComponent } from './events/event-details/event-details.component';
import { ConfirmationDialogComponent } from './_services/dialogs/confirmation/confirmation-dialog.component';
import { RolesComponent } from './user-roles/roles/roles.component';
import { UserRolesComponent } from './user-roles/user-roles.component';
import { SchoolsComponent } from './schools/schools.component';
import { EditSchoolDialogComponent } from './schools/edit-school-dialog/edit-school-dialog.component';
import { ViewSchoolDialogComponent } from './schools/view-school-dialog/view-school-dialog.component';
import { EditEventDetailDialogComponent } from './events/event-details/edit-event-detail/edit-event-detail-dialog.component';
import { CategoriesComponent } from './categories/categories.component';
import { EditCategoryDialogComponent } from './categories/edit-category-dialog/edit-category-dialog.component';
import { EditCategoryHOFDialogComponent } from './categories/edit-categoryHOF-dialog/edit-categoryHOF-dialog.component';
import { PageHeaderComponent } from './page-header/page-header.component';
import { EditEventDialogComponent } from './events/edit-event/edit-event.component';
import { HallOfFameComponent } from './hall-of-fame/hall-of-fame.component';
import { EditHallOfFameMemberDialogComponent } from './hall-of-fame/edit-hall-of-fame/edit-hall-of-fame.component';
import { CategoryEventsComponent } from './categories/category-events/category-events.component';
import { CategoryHallOfFameComponent } from './categories/category-hall-of-fame/category-hall-of-fame.component';
import { BulletinComponent } from './bulletin/bulletin.component';
import { EditBulletinDialogComponent } from './bulletin/edit-bulletin/edit-bulletin.component';
import { ErrorInterceptor } from './_helpers/error.interceptor';
import { FullCalendarModule } from '@fullcalendar/angular';
import dayGridPlugin from '@fullcalendar/daygrid'; // a plugin
import interactionPlugin from '@fullcalendar/interaction'; // a plugin
import { LoaderInterceptor } from './_helpers/loader.intercepter';
import { LoaderComponent } from './loader/loader.component';
import { EditorModule, TINYMCE_SCRIPT_SRC } from '@tinymce/tinymce-angular';
import { CssTypePipe } from './shared/pipes/css-type.pipe';
import { EditUserDialogComponent } from './user-roles/edit-user-dialog/edit-user-dialog.component';
import { EditRoleDialogComponent } from './user-roles/edit-role-dialog/edit-role-dialog.component';
import { MaintenanceComponent } from './maintenance/maintenance.component';
import { SidebarUserAreaComponent } from './layouts/sidebar-user-area/sidebar-user-area.component';
import { PageNotAuthroizedComponent } from './page-not-authroized/page-not-authroized.component';
import { AuthInterceptor } from './_helpers/interceptor';
import { CountdownDialogComponent } from './_services/dialogs/countdown/countdown.dialogcomponent';
import { LoginBoxDialogComponent } from './_services/dialogs/loginbox/loginbox.dialogcomponent';
import { MessageBoxDialogComponent } from './_services/dialogs/messagebox/messagebox.dialogcomponent';
import { LegislationItemsComponent } from './legislation/legislation-items/legislation-items.component';
import { LegislationComponent } from './legislation/legislation.component';
import { SplashDialogComponent } from './_services/splash/splash.service';
import { SafeHTMLPipe } from './shared/pipes/safehtml.pipe';
// import { FullCalendarWidgetModule } from 'projects/full-calendar-widget/src/app/full-calendar-widget.module';
import { SearchBarComponent } from './search-bar/search-bar.component';
import { CalendarWidgetComponent } from './calendar-widget/calendar-widget.component';
import { AddEditItemComponent } from './legislation/add-edit-item/add-edit-item.component';
import { LegislationRecipientsComponent } from './legislation/recipients-groups/email-recipients/email-recipients.component';
import { AddEditRecipientDialogComponent } from './legislation/recipients-groups/add-edit-recipient-dialog/add-edit-recipient-dialog.component';
import { LegislationFormComponent } from './legislation/legislation-form/legislation-form.component';
import { LegislationFormTemplateComponent } from './legislation/legislation-form/legislation-form-template/legislation-form-template.component';
import { AddEditLegislationGroupDialogComponent } from './legislation/recipients-groups/add-edit-group-dialog/add-edit-group-dialog.component';
import { RecipientsGroupsComponent } from './legislation/recipients-groups/recipients-groups.component';
import { LegislationGroupsComponent } from './legislation/recipients-groups/legislaiton-groups/legislation-groups.component';
import { GroupTagsComponent } from './legislation/recipients-groups/group-tags/group-tags.component';
import { NgxCaptchaModule } from 'ngx-captcha';
import { LegislativeFormWidgetComponent } from './legislation/legislation-form/legislative-form-widget.component';
import { FeedComponent } from './wordpress-feed/feed.component';
import { Feed2Component } from './wordpress-feed/feed2.component';
import { CarouselComponent } from './wordpress-feed/carousel/carousel.component';
import { CarouselItemDirective } from './wordpress-feed/carousel/carousel-item.directive';
import { CarouselItemElementDirective } from './wordpress-feed/carousel/carousel-item-element.directive';
import { DevTestPageComponent } from './dev-test-page/devtestpage.component';


FullCalendarModule.registerPlugins([
    // register FullCalendar plugins
    dayGridPlugin,
    interactionPlugin,
]);

@NgModule({
    declarations: [
        // Application components
        AppComponent,
        AddEditItemComponent,
        BulletinComponent,
        UsersComponent,
        CalendarWidgetComponent,
        CarouselComponent, 
        CarouselItemDirective, 
        CarouselItemElementDirective,
        CategoriesComponent,
        CategoryEventsComponent,
        CategoryHallOfFameComponent,
        CategoryTagsComponent,
        DevTestPageComponent,
        EventsComponent,
        EventDetailsComponent,
        FeedComponent,
        Feed2Component,
        GroupTagsComponent,
        HomeComponent,
        HallOfFameComponent,
        LegislationComponent,
        LegislationRecipientsComponent,
        LegislationFormComponent,
        LegislativeFormWidgetComponent,
        LegislationFormTemplateComponent,
        LegislationGroupsComponent,
        LegislationItemsComponent,
        RecipientsGroupsComponent,
        LoginComponent,
        MaintenanceComponent,
        PageNotAuthroizedComponent,
        PageNotFoundComponent,
        RolesComponent,
        SchoolsComponent,
        UserRolesComponent,

        // Dialog components
        AddEditLegislationGroupDialogComponent,
        AddEditRecipientDialogComponent,
        ConfirmationDialogComponent,
        CountdownDialogComponent,
        EditBulletinDialogComponent,
        EditCategoryDialogComponent,
        EditCategoryHOFDialogComponent,
        EditEventDetailDialogComponent,
        EditEventDialogComponent,
        EditHallOfFameMemberDialogComponent,
        EditRoleDialogComponent,
        EditSchoolDialogComponent,
        EditUserDialogComponent,
        LoginBoxDialogComponent,
        MessageBoxDialogComponent,
        SplashDialogComponent,
        ViewSchoolDialogComponent,

        // UI Components
        LoaderComponent,
        LoginLayoutComponent,
        MainLayoutComponent,
        NavbarComponent,
        PageHeaderComponent,
        SearchBarComponent,
        SidebarUserAreaComponent,
        ToolbarComponent,

        // Pipes
        CssTypePipe,
        SafeHTMLPipe,
    ],
    imports: [
        BrowserModule,
        BrowserAnimationsModule,
        FormsModule,
        HammerModule,
        ReactiveFormsModule,
        HttpClientModule,
        AppMaterialModule,
        JFlexLayoutModule,
        AppRoutingModule,
        NgxWebstorageModule.forRoot(),
        FullCalendarModule,
        EditorModule,
        NgxCaptchaModule,

        // Angular Elements
        // FullCalendarWidgetModule,
    ],
    schemas: [CUSTOM_ELEMENTS_SCHEMA],
    providers: [
        { provide: TINYMCE_SCRIPT_SRC, useValue: 'tinymce/tinymce.min.js' },
        { provide: HTTP_INTERCEPTORS, useClass: LoaderInterceptor, multi: true },
        { provide: HTTP_INTERCEPTORS, useClass: AuthInterceptor, multi: true },
        { provide: HTTP_INTERCEPTORS, useClass: ErrorInterceptor, multi: true },
    ],
    entryComponents: [
        // For all dialogs
        AddEditLegislationGroupDialogComponent,
        AddEditRecipientDialogComponent,
        ConfirmationDialogComponent,
        CountdownDialogComponent,
        EditBulletinDialogComponent,
        EditCategoryDialogComponent,
        EditCategoryHOFDialogComponent,
        EditEventDetailDialogComponent,
        EditEventDialogComponent,
        EditHallOfFameMemberDialogComponent,
        EditRoleDialogComponent,
        EditSchoolDialogComponent,
        EditUserDialogComponent,
        LoginBoxDialogComponent,
        MessageBoxDialogComponent,
        SplashDialogComponent,
        ViewSchoolDialogComponent,
    ],
    bootstrap: [AppComponent],
})
export class AppModule {}
